import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useQueries } from '@tanstack/vue-query';
import { pricingStructuresApi, type PricingStructure } from '@/api/pricing-structures';
import {
  pricingStructureQueryKey,
  transformPricingStructure,
  type TransformedPricingStructure,
} from './usePricingStructure';

const MIN_LOADING_DURATION_MS = 500;

export type PricingByCaregiverId = Record<number, TransformedPricingStructure | undefined>;

// What each query resolves to after `select`: the pricing tagged with its
// caregiver, so a result can never be attributed to the wrong caregiver.
export interface CaregiverPricingResult {
  caregiverId: number;
  pricing: TransformedPricingStructure;
}

// Same minimum-loading floor as usePricingStructure: a skeleton that flashes
// for a few milliseconds looks broken.
function useMinimumLoading(loading: Ref<boolean>): ComputedRef<boolean> {
  const minLoadingTimeElapsed = ref(true);
  let loadingTimer: ReturnType<typeof setTimeout> | null = null;

  watch(loading, (isLoading) => {
    if (isLoading) {
      if (loadingTimer) clearTimeout(loadingTimer);
      minLoadingTimeElapsed.value = false;
      loadingTimer = setTimeout(() => {
        minLoadingTimeElapsed.value = true;
      }, MIN_LOADING_DURATION_MS);
    }
  }, { immediate: true });

  return computed(() => loading.value || !minLoadingTimeElapsed.value);
}

interface StatusFlagsSource {
  isLoading: boolean;
  isError: boolean;
}

// Status flags carry no tag; while results lag the selection (useQueries
// re-syncs its observer one scheduler job after `caregiverIds` changes), report
// every caregiver as loading (never as failed) so nothing is pre-filled or
// blocked from a stale index.
function useStatusFlags(
  results: Readonly<Ref<ReadonlyArray<StatusFlagsSource>>>,
  caregiverIds: Ref<number[]>,
) {
  const resultsInSync = computed(() => results.value.length === caregiverIds.value.length);

  function flagByCaregiverId(flag: keyof StatusFlagsSource, outOfSyncValue: boolean) {
    const flags: Record<number, boolean> = {};
    caregiverIds.value.forEach((caregiverId, index) => {
      flags[caregiverId] = resultsInSync.value ? (results.value[index]?.[flag] ?? false) : outOfSyncValue;
    });

    return flags;
  }

  return {
    loadingByCaregiverId: computed(() => flagByCaregiverId('isLoading', true)),
    errorByCaregiverId: computed(() => flagByCaregiverId('isError', false)),
  };
}

// Multi-caregiver sibling of usePricingStructure: one query per caregiver on the
// same query key, so the cache is shared with the single-caregiver composable.
// A caregiver's pricing is `undefined` (not zeros) until its query has data, so
// callers can tell "not loaded" from "loaded with zeros".
export function usePricingStructures(
  patientId: Ref<number | undefined>,
  caregiverIds: Ref<number[]>,
  enabled: Ref<boolean> | boolean = true,
): {
  pricingByCaregiverId: ComputedRef<PricingByCaregiverId>;
  loadingByCaregiverId: ComputedRef<Record<number, boolean>>;
  errorByCaregiverId: ComputedRef<Record<number, boolean>>;
  isAnyLoading: ComputedRef<boolean>;
  hasAnyError: ComputedRef<boolean>;
} {
  const isEnabled = computed(() => (typeof enabled === 'boolean' ? enabled : enabled.value));

  const queries = computed(() => {
    // Captured once per rebuild so key, fetch and tag always agree on the patient.
    const currentPatientId = patientId.value;

    return caregiverIds.value.map((caregiverId) => ({
      queryKey: pricingStructureQueryKey(currentPatientId, caregiverId),
      queryFn: (): Promise<PricingStructure | undefined> => {
        if (!currentPatientId || !caregiverId) return Promise.resolve(undefined);

        return pricingStructuresApi.show(currentPatientId, caregiverId);
      },
      select: (data: PricingStructure | undefined): CaregiverPricingResult | undefined =>
        (data ? { caregiverId, pricing: transformPricingStructure(data) } : undefined),
      enabled: isEnabled.value && !!currentPatientId && !!caregiverId,
    }));
  });

  const results = useQueries({ queries });

  // `results` is positional and updates one scheduler job after `caregiverIds`
  // (useQueries re-syncs its observer in a watcher), so the data map is built
  // from each result's own tag, never from its index.
  const pricingByCaregiverId = computed<PricingByCaregiverId>(() => {
    const pricing: PricingByCaregiverId = {};
    caregiverIds.value.forEach((caregiverId) => {
      pricing[caregiverId] = undefined;
    });
    results.value.forEach((result) => {
      const data = result.data as CaregiverPricingResult | undefined;
      if (data && caregiverIds.value.includes(data.caregiverId)) pricing[data.caregiverId] = data.pricing;
    });

    return pricing;
  });

  const { loadingByCaregiverId, errorByCaregiverId } = useStatusFlags(results, caregiverIds);

  const anyQueryLoading = computed(() => Object.values(loadingByCaregiverId.value).some(Boolean));
  const isAnyLoading = useMinimumLoading(anyQueryLoading);
  const hasAnyError = computed(() => Object.values(errorByCaregiverId.value).some(Boolean));

  return {
    pricingByCaregiverId,
    loadingByCaregiverId,
    errorByCaregiverId,
    isAnyLoading,
    hasAnyError,
  };
}
